import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Alert
} from '@mui/material';
import useAutoFocus from '../hooks/useAutoFocus';
import RichTextField from './editor/RichTextField';
import { useAuth } from '../contexts/AuthContext';
import { iamApi } from '../services/iamApi';
import FieldRenderer from './itemDetails/FieldRenderer';

/**
 * AddDialog component - Allows creating new items or containers
 * 
 * @component
 */
const AddDialog = ({
  open,
  onClose,
  onAdd,
  parentId = null, // Default parameter instead of using defaultProps
  isContainer = false, // Default parameter instead of using defaultProps
  projectId = null, // #197 — see comment below
  parentPath = null, // #275 follow-up — breadcrumb of where the new item will land
}) => {
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState(isContainer ? 'container' : 'requirement');
  const [error, setError] = useState('');
  // #793: rendered unconditionally by the caller with `open` toggling — a bare
  // `autoFocus` only fires on the FIRST mount, so every reopen after the first
  // silently lost focus. See hooks/useAutoFocus.
  const focusRef = useAutoFocus(open);

  // Custom project-attached item types (#197) — Requirement/Container remain the
  // hardcoded, always-available defaults; any OTHER type the project has enabled
  // (system, e.g. `test_case`, or custom, e.g. a project-defined `hazard`) is listed
  // alongside them so an item can be created with its real type from the start,
  // instead of being permanently stuck with the system requirement/container type.
  //
  // Project context comes from the `projectId` PROP (the caller resolves it — e.g.
  // ItemDetails.js has the currently-loaded item's own `project_id`; the tree view
  // resolves it from the add's parent node or the single visible project), NOT from
  // `AuthContext`'s `activeProject`: that's driven by an org/project switcher
  // (`SelectContent`) that isn't actually wired into the current app shell, so it is
  // never set in the running app — relying on it here would make this selector
  // silently never populate. `useAuth()?.activeProject` is kept ONLY as a harmless
  // fallback in case a caller doesn't have a better answer (and for forward
  // compatibility if a project switcher is reintroduced) — it is not the primary
  // signal. `useAuth()` returns `null` outside an `<AuthProvider>` (e.g. this
  // component's own unit tests) — guarded with optional chaining.
  const auth = useAuth();
  const resolvedProjectId = projectId || auth?.activeProject || null;
  const [itemTypes, setItemTypes] = useState([]);

  // Fetch the project's item types whenever the dialog opens (fresh each time, in
  // case a type was added/removed since it was last opened). Keeps each type's
  // full `fields` array (not just key/label) so the main-area fields (#247) can
  // be derived per-type without a second round trip when the selection changes.
  React.useEffect(() => {
    if (!open || !resolvedProjectId) {
      setItemTypes([]);
      return undefined;
    }
    let cancelled = false;
    iamApi.projectItemTypes(resolvedProjectId)
      .then((types) => {
        if (cancelled) return;
        setItemTypes((types || []).filter((t) => t.key !== 'requirement' && t.key !== 'container'));
      })
      .catch(() => { if (!cancelled) setItemTypes([]); }); // non-critical — dialog still offers Requirement/Container
    return () => { cancelled = true; };
  }, [open, resolvedProjectId]);

  // Main-area attribute values for the currently-selected CUSTOM type (#247) —
  // keyed by field key, same raw shape FieldRenderer/attributesToRows use.
  const [fieldValues, setFieldValues] = useState({});

  // Reset form when dialog opens or closes
  React.useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setType(isContainer ? 'container' : 'requirement');
      setFieldValues({});
      setError('');
    }
  }, [open, isContainer]);

  // The human label for the dialog title — falls back to the plain key for a
  // custom type whose label hasn't loaded yet.
  const selectedTypeLabel = (() => {
    if (type === 'container') return 'Container';
    if (type === 'requirement') return 'Requirement';
    const t = itemTypes.find((it) => it.key === type);
    return t?.label || type;
  })();

  // Main-area fields (#247) for the selected type — same "show_in_main" filter
  // Item Detail's main form area uses (#196/#197), so the create dialog and the
  // detail view present the identical set of fields for a given type. [] for
  // the built-in Requirement/Container types (no meta-model fields) or before
  // the type list has loaded.
  const mainFields = (() => {
    if (type === 'container' || type === 'requirement') return [];
    const t = itemTypes.find((it) => it.key === type);
    return (t?.fields || []).filter((f) => f.show_in_main);
  })();

  // Changing the type clears any values entered against the PREVIOUS type's
  // fields — they don't apply to the newly-selected type and stale values
  // would otherwise silently ride along into `attributes` on submit.
  const handleTypeChange = (newType) => {
    setType(newType);
    setFieldValues({});
  };

  const getFieldValue = (key) => fieldValues[key] ?? '';
  const setFieldValue = (key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }));

  // Handle title input with validation
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    if (e.target.value.trim() !== '') {
      setError('');
    }
  };

  // Handle dialog submission
  const handleSubmit = () => {
    // Validate title
    if (title.trim() === '') {
      setError('Title is required');
      return;
    }

    // Create item/container object. `attributes.type` stays the plain
    // 'requirement'/'container' flag the backend uses to pick the create branch
    // (container vs. regular item); a CUSTOM type is carried separately as a
    // top-level `type` key (#197) — resolved server-side (system OR custom key)
    // instead of the old hardcoded system-type-only behaviour.
    const isBuiltinContainer = type === 'container';
    const newItem = {
      title: title.trim(),
      description: description.trim(),
      attributes: {
        type: isBuiltinContainer ? 'container' : 'requirement',
        // #247: the selected type's main-area fields, entered up front instead
        // of only being settable after the fact on the Item Detail page. Same
        // storage shape Item Detail's own main-area fields read/write
        // (attributes keyed by field key) — same rows the Attributes tab sees.
        ...fieldValues,
      },
      parent_id: parentId,
      // #287: for a TOP-LEVEL create (no parent to inherit a project from —
      // see `resolve_target_project`), the backend only knows the target
      // project from the `X-Project-Id` header, never the request body. Without
      // this, an explicit "New Item" from a SPECIFIC project's own context menu
      // (with other projects also in scope) had no way to reach the right one —
      // `requirementsApi.createItem` turns this into that header.
      project_id: resolvedProjectId || undefined,
    };
    if (type !== 'requirement' && type !== 'container') {
      newItem.type = type;
    }

    // Call the onAdd callback with the new item data
    onAdd(newItem);
  };

  // Handle enter key press in title field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-dialog-title"
    >
      <DialogTitle id="add-dialog-title">
        {`Add ${selectedTypeLabel}`}
      </DialogTitle>
      <DialogContent>
        {/* #275 follow-up: show exactly where this will land, rather than the
            user having to infer it from whatever was selected in the tree. */}
        {parentPath && (
          <Typography
            data-testid="add-dialog-parent-path"
            variant="body2"
            sx={{ mb: 2, color: 'text.secondary' }}
          >
            Adding to: {parentPath}
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Type selector (#247 follow-up): a labelled select, matching the ONE
            canonical style used everywhere else a type is picked (e.g. Item
            Detail's own retype control) — a radio-button row visibly reflowed
            differently depending on how many types were on offer, which is
            why this dialog looked different depending on where it was opened
            from (a project with custom types vs. one with none resolved).
            A select renders identically either way. */}
        <TextField
          select
          label="Type"
          size="small"
          fullWidth
          data-testid="add-dialog-type-select"
          value={type}
          onChange={(e) => handleTypeChange(e.target.value)}
          sx={{ mb: 3, mt: 1 }}
        >
          <MenuItem value="requirement">Requirement</MenuItem>
          <MenuItem value="container">Container</MenuItem>
          {itemTypes.map((t) => (
            <MenuItem key={t.id} value={t.key}>{t.label}</MenuItem>
          ))}
        </TextField>

        {/* Main-area attribute fields (#247) for the selected type — reloads
            whenever `type` changes (see mainFields/handleTypeChange above),
            same field set Item Detail's own main form area shows. */}
        {mainFields.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {mainFields.map((f) => (
              <FieldRenderer key={f.id} field={f} value={getFieldValue(f.key)}
                onChange={(v) => setFieldValue(f.key, v)} />
            ))}
          </Box>
        )}

        <TextField
          inputRef={focusRef}
          margin="dense"
          id="title"
          label="Title"
          type="text"
          fullWidth
          value={title}
          onChange={handleTitleChange}
          onKeyPress={handleKeyPress}
          error={!!error}
          helperText={error || "Required"}
          sx={{ mb: 3 }}
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
            Description
          </Typography>
          <RichTextField
            value={description}
            onChange={(content) => setDescription(content)}
            minHeight={380}
            tinymceInit={{
              // Fixed height (no autoresize): the autoresize plugin collapsed an
              // empty new-requirement editor to ~150px regardless of
              // autoresize_min_height, making the description box too small (T1.14).
              height: 380,
              min_height: 380,
              menubar: false,
              statusbar: true, // Enable status bar for resize handle
              resize: 'both', // Allow vertical and horizontal resizing
              plugins: ['image', 'table', 'link', 'lists', 'code'],
              toolbar: 'bold italic | formatselect | bullist numlist | link image table code',
              content_style: `
                body {
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                  font-size: 14px;
                  margin: 8px;
                  padding: 4px;
                }
              `,
              toolbar_location: 'top',
              toolbar_sticky: true,
              skin: 'oxide',
              content_css: 'default',
              branding: false,
              elementpath: false,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  parentId: PropTypes.string,
  isContainer: PropTypes.bool,
  projectId: PropTypes.string,
  parentPath: PropTypes.string,
};

// Removed defaultProps definition since we're using default parameters in the function signature

export default AddDialog;
